/**
 * AuthorImportService — validate a SelfAuthorExportEnvelope and write its
 * payload into the selected author workspace.
 *
 * Two responsibilities:
 *   - validate(json)        Quick envelope-shape check + summary for the
 *                           AuthorImportDialog preview. NO Firestore writes.
 *   - replaceAuthor(opts)   Cascade-delete the selected target author, then
 *                           write the entire imported tree. projectId and
 *                           authorId are rewritten on every doc.
 *
 * "Replace, never merge" — the selected author workspace is replaced. The
 * project still maintains exactly one baseline default author.
 *
 * Sanitization on import:
 *   - Strip Author.onboardingTaskId (points to backend_tasks in source proj)
 *   - Strip Author.targetAudienceIds (audiences not exported, refs would dangle)
 *   - Force Author.kind='self' + preserve/derive the baseline default marker
 *   - Rewrite projectId and authorId on every author-owned doc
 *   - Run stripUndefined on every payload before setDoc (Sprint 16 bug A7)
 */

import {
  collection,
  doc,
  serverTimestamp,
  writeBatch,
  type DocumentReference,
  type WriteBatch,
} from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import { stripUndefined } from '../utils/stripUndefined';
import { authorFirestoreService } from './AuthorFirestoreService';
import { cascadeDeleteSelfAuthor, type CascadeDeleteCounts } from './authorCascadeDelete';
import type { SelfAuthorExportEnvelope, SelfAuthorExportPayload } from './AuthorExportService';

const BATCH_CHUNK_SIZE = 400;

// ─────────────────────────────────────────────────────────────────────────
// Validation
// ─────────────────────────────────────────────────────────────────────────

export interface ImportPreviewSummary {
  displayName: string;
  authorId: string;
  sourceProjectId: string;
  exportedAt: string;
  platformCount: number;
  postCount: number;
  pillarCount: number;
  templatesCount: number;
  examplesCount: number;
  formatsCount: number;
  designDocsCount: number;
  designProfileSetsCount: number;
  designProfilesCount: number;
  designReferencesCount: number;
  platforms: Array<{ id: string; displayName: string; postCount: number }>;
}

export interface ValidateResult {
  valid: boolean;
  envelope?: SelfAuthorExportEnvelope;
  summary?: ImportPreviewSummary;
  errors: string[];
}

/**
 * Cheap structural validation. No Firestore reads. Used by the dialog to
 * show a preview card before the user commits to importing.
 */
export function validateAuthorExport(json: unknown): ValidateResult {
  const errors: string[] = [];

  if (!json || typeof json !== 'object') {
    return { valid: false, errors: ['File is empty or not a JSON object.'] };
  }
  const j = json as Record<string, unknown>;

  if (j.__segmently_self_author_export__ !== true) {
    errors.push('Not a Segmently self-author export (missing discriminator).');
  }
  if (j.version !== '1.0.0') {
    errors.push(`Unsupported export version "${String(j.version)}". Expected "1.0.0".`);
  }
  if (j.type !== 'self-author') {
    errors.push(`Unsupported export type "${String(j.type)}". Expected "self-author".`);
  }

  const payload = j.payload as SelfAuthorExportPayload | undefined;
  if (!payload || typeof payload !== 'object') {
    errors.push('Missing or invalid payload.');
    return { valid: false, errors };
  }
  if (!payload.author || typeof payload.author !== 'object') {
    errors.push('Missing payload.author.');
  }
  if (payload.author && !(payload.author as { id?: string }).id) {
    errors.push('Author has no id.');
  }
  if (payload.author && !(payload.author as { displayName?: string }).displayName) {
    errors.push('Author has no displayName.');
  }
  if (!Array.isArray(payload.platforms)) {
    errors.push('payload.platforms must be an array.');
  }
  if (!Array.isArray(payload.topicPillars)) {
    errors.push('payload.topicPillars must be an array.');
  }

  if (errors.length > 0) return { valid: false, errors };

  const envelope = j as unknown as SelfAuthorExportEnvelope;
  const summary: ImportPreviewSummary = {
    displayName: envelope.payload.author.displayName ?? '(unknown)',
    authorId: envelope.payload.author.id ?? '',
    sourceProjectId: envelope.metadata.sourceProjectId,
    exportedAt: envelope.metadata.exportedAt,
    platformCount: envelope.payload.platforms.length,
    postCount: envelope.payload.platforms.reduce(
      (sum, p) => sum + (p.authorPosts?.length ?? 0), 0,
    ),
    pillarCount: envelope.payload.topicPillars.length,
    templatesCount: envelope.payload.platforms.reduce(
      (sum, p) => sum + (p.postTemplates?.length ?? 0), 0,
    ),
    examplesCount: envelope.payload.platforms.reduce(
      (sum, p) => sum + (p.postExamples?.length ?? 0), 0,
    ),
    formatsCount: envelope.payload.platforms.reduce(
      (sum, p) => sum + (p.formats?.length ?? 0), 0,
    ),
    designDocsCount: envelope.payload.platforms.reduce(
      (sum, p) => sum + (p.designDocs?.length ?? 0), 0,
    ),
    designProfileSetsCount: envelope.payload.platforms.reduce(
      (sum, p) => sum + (p.designProfileSets?.length ?? 0), 0,
    ),
    designProfilesCount: envelope.payload.platforms.reduce(
      (sum, p) => sum + (p.designProfileSets ?? []).reduce(
        (inner, set) => inner + (set.profiles?.length ?? 0),
        0,
      ),
      0,
    ),
    designReferencesCount: envelope.payload.platforms.reduce(
      (sum, p) => sum + (p.designProfileSets ?? []).reduce(
        (inner, set) => inner + (set.profiles ?? []).reduce(
          (profileSum, profile) => profileSum + (profile.references?.length ?? 0),
          0,
        ),
        0,
      ),
      0,
    ),
    platforms: envelope.payload.platforms.map((p) => ({
      id: (p.platform.id as string) ?? (p.platform.platformId as string) ?? 'unknown',
      displayName: (p.platform.displayName as string) ?? (p.platform.id as string) ?? 'Platform',
      postCount: p.authorPosts?.length ?? 0,
    })),
  };

  return { valid: true, envelope, summary, errors: [] };
}

// ─────────────────────────────────────────────────────────────────────────
// Sanitization
// ─────────────────────────────────────────────────────────────────────────

/**
 * Author-level rewrites: force self-author invariants, strip refs that
 * dangle across project boundaries, rewrite projectId.
 * Returns a new object — input is not mutated.
 */
function sanitizeAuthor(
  author: Record<string, unknown>,
  targetProjectId: string,
  targetAuthorId: string,
  isDefault: boolean,
): Record<string, unknown> {
  const cleaned = { ...author };
  delete cleaned.onboardingTaskId;     // points to backend_tasks/{taskId} in source project
  delete cleaned.targetAudienceIds;    // audiences not exported; refs dangle
  cleaned.id = targetAuthorId;
  cleaned.projectId = targetProjectId;
  cleaned.authorId = targetAuthorId;
  cleaned.kind = 'self';
  cleaned.isDefault = isDefault;
  return stripUndefined(cleaned) as Record<string, unknown>;
}

/** Generic rewrite for docs owned by a specific self-author. */
function rewriteProjectAuthorId(
  payload: Record<string, unknown>,
  targetProjectId: string,
  targetAuthorId: string,
  overrides: Record<string, unknown> = {},
): Record<string, unknown> {
  return stripUndefined({
    ...payload,
    projectId: targetProjectId,
    authorId: targetAuthorId,
    ...overrides,
  }) as Record<string, unknown>;
}

// ─────────────────────────────────────────────────────────────────────────
// Service
// ─────────────────────────────────────────────────────────────────────────

export interface ReplaceAuthorOptions {
  targetProjectId: string;
  payload: SelfAuthorExportPayload;
  /** Author workspace to replace. Defaults to the imported payload id. */
  replaceAuthorId?: string | null;
  /** Whether the imported target should become the baseline default. */
  makeDefault?: boolean;
  /** Optional progress reporter — receives short status strings. */
  onProgress?: (msg: string) => void;
}

export interface ReplaceAuthorResult {
  /** Counts of docs deleted from any pre-existing self-author. Zero counts when target was empty. */
  deletedCounts: CascadeDeleteCounts;
  /** Counts of docs written from the import payload. */
  writtenCounts: {
    author: number;
    platforms: number;
    voiceDocs: number;
    designDocs: number;
    designProfileSets: number;
    designProfiles: number;
    designReferences: number;
    formats: number;
    postExamples: number;
    postTemplates: number;
    authorPosts: number;
    topicPillars: number;
    creatorReferences: number;
  };
}

export class AuthorImportService {
  private get db() {
    return CoreFirebaseService.getInstance().firestore;
  }

  validate = validateAuthorExport;

  /**
   * Cascade-delete the selected target self-author in `targetProjectId`, then
   * write every doc from `payload` into that author workspace.
   *
   * If `replaceAuthorId` is omitted, the imported author id is used. The
   * imported author becomes the default only when replacing a default author,
   * when there is no default author yet, or when `makeDefault` is explicit.
   */
  async replaceAuthor(opts: ReplaceAuthorOptions): Promise<ReplaceAuthorResult> {
    const { targetProjectId, payload, onProgress } = opts;
    const importedAuthorId = payload.author.id;
    if (!importedAuthorId) {
      throw new Error('Imported author has no id field.');
    }
    const targetAuthorId = opts.replaceAuthorId || importedAuthorId;

    // 1. Cascade-delete the selected target author.
    onProgress?.('Checking for existing creator…');
    const [existing, defaultAuthor] = await Promise.all([
      authorFirestoreService.getAuthorById(targetProjectId, targetAuthorId),
      authorFirestoreService.getDefaultAuthor(targetProjectId),
    ]);
    const makeDefault = opts.makeDefault ?? Boolean(existing?.isDefault || !defaultAuthor);
    let deletedCounts: CascadeDeleteCounts = {
      authorPosts: 0, postTemplates: 0, postExamples: 0, formats: 0,
      voiceDocs: 0, designDocs: 0, designProfileSets: 0, designProfiles: 0,
      designReferences: 0, platforms: 0, topicPillars: 0, creatorReferences: 0,
      contentStrategies: 0, topics: 0, contentPosts: 0,
      topicGenerationRuns: 0, topicGenerationDrafts: 0,
      postGenerationRuns: 0, postGenerationDrafts: 0,
      researchRuns: 0, backendTasks: 0,
    };
    if (existing && existing.id) {
      onProgress?.('Deleting existing creator…');
      deletedCounts = await cascadeDeleteSelfAuthor(targetProjectId, existing.id);
    }

    // 2. Write the imported tree, chunked at 400 ops/batch.
    onProgress?.('Writing imported author…');
    const writtenCounts = {
      author: 0, platforms: 0, voiceDocs: 0,
      designDocs: 0, designProfileSets: 0, designProfiles: 0, designReferences: 0,
      formats: 0,
      postExamples: 0, postTemplates: 0, authorPosts: 0,
      topicPillars: 0, creatorReferences: 0,
    };

    // 2a. Author root doc — single-doc batch (kept separate for clarity).
    const authorRef = doc(this.db, 'projects', targetProjectId, 'authors', targetAuthorId);
    const authorPayload = sanitizeAuthor(
      payload.author as unknown as Record<string, unknown>,
      targetProjectId,
      targetAuthorId,
      makeDefault,
    );
    // Stamp updatedAt to mark the import time; preserve createdAt if present.
    authorPayload.updatedAt = serverTimestamp();
    if (!authorPayload.createdAt) authorPayload.createdAt = serverTimestamp();
    {
      const batch = writeBatch(this.db);
      batch.set(authorRef, authorPayload);
      await batch.commit();
      writtenCounts.author = 1;
    }

    // 2b. Per-platform writes.
    for (const bundle of payload.platforms) {
      const platformId = (bundle.platform.id as string) ?? (bundle.platform.platformId as string);
      if (!platformId) {
        throw new Error('Imported platform has no id / platformId field.');
      }

      onProgress?.(`Writing platform ${(bundle.platform.displayName as string) ?? platformId}…`);

      const platformRef = doc(
        this.db, 'projects', targetProjectId, 'authors', targetAuthorId,
        'platforms', platformId,
      );
      // Platform doc — usually small, single-doc batch.
      {
        const batch = writeBatch(this.db);
        batch.set(platformRef, rewriteProjectAuthorId(
          bundle.platform as Record<string, unknown>,
          targetProjectId,
          targetAuthorId,
          { id: platformId },
        ));
        await batch.commit();
        writtenCounts.platforms += 1;
      }

      // voice/voice (single-doc subcollection).
      if (bundle.voice) {
        const voiceRef = doc(
          this.db, 'projects', targetProjectId, 'authors', targetAuthorId,
          'platforms', platformId, 'voice', 'voice',
        );
        const batch = writeBatch(this.db);
        batch.set(voiceRef, rewriteProjectAuthorId(
          bundle.voice as unknown as Record<string, unknown>,
          targetProjectId,
          targetAuthorId,
        ));
        await batch.commit();
        writtenCounts.voiceDocs += 1;
      }

      writtenCounts.designDocs += await this.writeCollection(
        ['projects', targetProjectId, 'authors', targetAuthorId, 'platforms', platformId, 'design'],
        (bundle.designDocs ?? []) as Array<Record<string, unknown> & { id?: string }>,
        targetProjectId,
        targetAuthorId,
      );
      const designProfileCounts = await this.writeDesignProfileSets(
        targetProjectId,
        targetAuthorId,
        platformId,
        bundle.designProfileSets ?? [],
      );
      writtenCounts.designProfileSets += designProfileCounts.sets;
      writtenCounts.designProfiles += designProfileCounts.profiles;
      writtenCounts.designReferences += designProfileCounts.references;

      // formats / post_examples / post_templates / author_posts → chunked sets.
      writtenCounts.formats += await this.writeCollection(
        ['projects', targetProjectId, 'authors', targetAuthorId, 'platforms', platformId, 'formats'],
        bundle.formats as Array<Record<string, unknown> & { id?: string }>,
        targetProjectId,
        targetAuthorId,
      );
      writtenCounts.postExamples += await this.writeCollection(
        ['projects', targetProjectId, 'authors', targetAuthorId, 'platforms', platformId, 'post_examples'],
        bundle.postExamples,
        targetProjectId,
        targetAuthorId,
      );
      writtenCounts.postTemplates += await this.writeCollection(
        ['projects', targetProjectId, 'authors', targetAuthorId, 'platforms', platformId, 'post_templates'],
        bundle.postTemplates as unknown as Array<Record<string, unknown> & { id?: string }>,
        targetProjectId,
        targetAuthorId,
      );
      writtenCounts.authorPosts += await this.writeCollection(
        ['projects', targetProjectId, 'authors', targetAuthorId, 'platforms', platformId, 'author_posts'],
        bundle.authorPosts as unknown as Array<Record<string, unknown> & { id?: string }>,
        targetProjectId,
        targetAuthorId,
      );
    }

    // 2c. topic_pillars (project-level sibling).
    onProgress?.('Writing topic pillars…');
    writtenCounts.topicPillars = await this.writeCollection(
      ['projects', targetProjectId, 'topic_pillars'],
      payload.topicPillars as unknown as Array<Record<string, unknown> & { id?: string }>,
      targetProjectId,
      targetAuthorId,
      { authorIds: [targetAuthorId] },
    );

    // 2d. creator_references self-ref (deterministic id).
    if (payload.creatorReference) {
      const crefRef = doc(
        this.db, 'projects', targetProjectId, 'creator_references',
        `cref_self_${targetAuthorId}`,
      );
      const batch = writeBatch(this.db);
      batch.set(crefRef, rewriteProjectAuthorId(
        payload.creatorReference as Record<string, unknown>,
        targetProjectId,
        targetAuthorId,
        { id: `cref_self_${targetAuthorId}` },
      ));
      await batch.commit();
      writtenCounts.creatorReferences = 1;
    }

    if (makeDefault) {
      await authorFirestoreService.setDefaultAuthor(targetProjectId, targetAuthorId);
    }

    onProgress?.('Done.');
    return { deletedCounts, writtenCounts };
  }

  /**
   * Write an array of docs to a collection, chunked at 400 ops/batch.
   * Each doc's `id` field (or generated random id if absent) is used as
   * the Firestore doc id. Returns the count written.
   */
  private async writeCollection(
    pathSegments: ReadonlyArray<string>,
    docs: ReadonlyArray<Record<string, unknown> & { id?: string }>,
    targetProjectId: string,
    targetAuthorId: string,
    overrides: Record<string, unknown> = {},
  ): Promise<number> {
    if (docs.length === 0) return 0;
    const colRef = collection(this.db, ...(pathSegments as [string, ...string[]]));

    for (let i = 0; i < docs.length; i += BATCH_CHUNK_SIZE) {
      const chunk = docs.slice(i, i + BATCH_CHUNK_SIZE);
      const batch: WriteBatch = writeBatch(this.db);
      for (const docData of chunk) {
        const id = (docData.id as string | undefined) ?? doc(colRef).id;
        const ref: DocumentReference = doc(colRef, id);
        const payload = rewriteProjectAuthorId({ ...docData, id }, targetProjectId, targetAuthorId, overrides);
        batch.set(ref, payload);
      }
      await batch.commit();
    }
    return docs.length;
  }

  private async writeDesignProfileSets(
    targetProjectId: string,
    targetAuthorId: string,
    platformId: string,
    sets: NonNullable<SelfAuthorExportPayload['platforms'][number]['designProfileSets']>,
  ): Promise<{ sets: number; profiles: number; references: number }> {
    let profileCount = 0;
    let referenceCount = 0;
    for (const profileSet of sets) {
      const setId = profileSet.set.id;
      if (!setId) continue;
      const setRef = doc(
        this.db,
        'projects', targetProjectId, 'authors', targetAuthorId,
        'platforms', platformId, 'design_profile_sets', setId,
      );
      const batch = writeBatch(this.db);
      batch.set(setRef, rewriteProjectAuthorId(profileSet.set, targetProjectId, targetAuthorId, { id: setId }));
      await batch.commit();

      profileCount += await this.writeCollection(
        ['projects', targetProjectId, 'authors', targetAuthorId, 'platforms', platformId, 'design_profile_sets', setId, 'profiles'],
        profileSet.profiles,
        targetProjectId,
        targetAuthorId,
      );
      for (const profile of profileSet.profiles) {
        referenceCount += await this.writeCollection(
          ['projects', targetProjectId, 'authors', targetAuthorId, 'platforms', platformId, 'design_profile_sets', setId, 'profiles', profile.id, 'references'],
          profile.references ?? [],
          targetProjectId,
          targetAuthorId,
        );
      }
    }
    return { sets: sets.length, profiles: profileCount, references: referenceCount };
  }
}

export const authorImportService = new AuthorImportService();
